import React, { useEffect, useState, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { PAGES } from 'constants/pages';

import {
    GetManualSupplyOrdersQuery,
    GetManualSupplyOrderSupplierProductsStockVariationQuery,
    useUpdateManualSupplyOrdersMutation,
    useAddProductsToManualSupplyOrderMutation,
    TotadminPermission,
} from 'data/__generated__';

import { PageTitle } from 'components/PageTitle';
import { ActionsAndSave } from 'components/PageHeader';
import { TrelloCardInfoPopup } from 'pages/SupplyOrders/Popups/TrelloCardInfoPopup';

import { SupplierStatusSection } from './sections/SupplierStatusSection';
import { ProductsInfoSection } from './sections/ProductsInfoSection';
import { AddProductsToSupplierModal } from './components/AddProductsToSupplierModal';

import { Header, HeaderTitle } from 'components/Header';
import { zodResolver } from '@hookform/resolvers/zod';
import { ManualSupplyOrdersActionBar } from 'pages/ManualSupplyOrders/ManualSupplyOrdersActionBar';
import { handleUpdateResults } from 'handlers';

// Schéma Zod pour la validation des commandes fournisseurs manuelles
const supplyOrderProductSchema = z.object({
    _id: z.string(),
    supplyOrderId: z.string(), // used to change the supply order
    colis: z.number().min(0).optional().nullable(),
    confirmedQuantity: z.number().int().min(0).optional().nullable(),
    conditionning: z.number().int().min(1),
    packagesPerBatch: z.number().int().min(1),
    unitPrice: z.number().min(0),
});

const supplyOrderUpdateSchema = z.object({
    _id: z.string(),
    comment: z.string().optional().nullable(),
    dateDeliveryScheduled: z.string().optional().nullable(),
    description: z.string().optional().nullable(),
    orderDate: z.string().optional().nullable(),
    nextOrderDate: z.string().optional().nullable(),
    priceHTInvoice: z.number().optional().nullable(),
    priceTTCInvoice: z.number().optional().nullable(),
    products: z.array(supplyOrderProductSchema),
});

const manualSupplyOrdersSchema = z.object({
    supplyOrders: z.array(supplyOrderUpdateSchema),
});

export type ManualSupplyOrdersFormData = z.infer<typeof manualSupplyOrdersSchema>;

type ManualSupplyOrdersFormProps = {
    manualSupplyOrders: GetManualSupplyOrdersQuery['manualSupplyOrders'];
    stockVariationData?: GetManualSupplyOrderSupplierProductsStockVariationQuery['manualSupplyOrderSupplierProductsStockVariation'];
    stockVariationLoading?: boolean;
    stockVariationError?: Error | null;
    trelloCardInfoPopup: boolean;
    setTrelloCardInfoPopup: (isOpen: boolean) => void;
};

export const ManualSupplyOrdersForm: React.FC<ManualSupplyOrdersFormProps> = ({
    manualSupplyOrders,
    stockVariationData,
    stockVariationLoading,
    stockVariationError,
    trelloCardInfoPopup,
    setTrelloCardInfoPopup,
}) => {
    const [selectedSupplyOrderIds, setSelectedSupplyOrderIds] = useState<string[]>([]);
    const [updateManualSupplyOrders, { loading: updateLoading }] = useUpdateManualSupplyOrdersMutation();
    const [addProductsToManualSupplyOrder, { loading: addProductsLoading }] =
        useAddProductsToManualSupplyOrderMutation();
    const [updateStockVariationsLoading, setUpdateStockVariationsLoading] = useState<boolean>(false);
    const [updateForecastsLoading, setUpdateForecastsLoading] = useState<boolean>(false);
    const [isAddProductsModalOpen, setIsAddProductsModalOpen] = useState<boolean>(false);

    const methods = useForm<ManualSupplyOrdersFormData>({
        resolver: zodResolver(manualSupplyOrdersSchema),
        defaultValues: { supplyOrders: manualSupplyOrders },
        mode: 'all',
    });

    const {
        handleSubmit,
        formState: { isDirty, isValid },
        reset,
    } = methods;

    useEffect(() => {
        if (manualSupplyOrders) {
            methods.reset({ supplyOrders: manualSupplyOrders });
            setSelectedSupplyOrderIds([]);
        }
    }, [manualSupplyOrders, reset, setSelectedSupplyOrderIds]);

    const onSubmit = handleSubmit(async (formData) => {
        const allProducts = formData.supplyOrders.flatMap((supplyOrder) => supplyOrder.products);
        const updatedSupplyOrders = formData.supplyOrders.map((supplyOrder) => {
            return {
                _id: supplyOrder._id,
                orderDate: supplyOrder.orderDate,
                nextOrderDate: supplyOrder.nextOrderDate,
                dateDeliveryScheduled: supplyOrder.dateDeliveryScheduled,
                comment: supplyOrder.comment,
                description: supplyOrder.description,
                priceHTInvoice: supplyOrder.priceHTInvoice,
                priceTTCInvoice: supplyOrder.priceTTCInvoice,
                products: allProducts
                    .filter((product) => product.supplyOrderId === supplyOrder._id && product.colis !== null)
                    .map((product) => ({
                        _id: product._id,
                        colis: product.colis!, // filtered before
                        unitPrice: product.unitPrice,
                        confirmedQuantity: product.confirmedQuantity,
                        conditionning: product.conditionning,
                        packagesPerBatch: product.packagesPerBatch,
                    })),
            };
        });

        const { data, errors } = await updateManualSupplyOrders({
            variables: {
                supplyOrders: updatedSupplyOrders,
            },
        });

        handleUpdateResults({
            data: data?.updateManualSupplyOrders,
            errors,
            errorMessage: 'Erreur lors de la mise à jour des commandes',
            successMessage: 'Les commandes ont été mises à jour avec succès !',
        });
    });

    const handleUpdateQuantitiesFromPredictions = useCallback(() => {
        if (selectedSupplyOrderIds.length === 0) {
            toast.warning('Veuillez sélectionner au moins une commande');
            return;
        }

        const { setValue, getValues } = methods;
        const formData = getValues();
        let updatedCount = 0;

        // Parcourir toutes les commandes sélectionnées
        formData.supplyOrders.forEach((supplyOrder, supplyOrderIndex) => {
            if (selectedSupplyOrderIds.includes(supplyOrder._id)) {
                // Parcourir tous les produits de cette commande
                const originalSupplyOrder = manualSupplyOrders.find((so) => so._id === supplyOrder._id);
                if (!originalSupplyOrder) return;
                supplyOrder.products.forEach((product, productIndex) => {
                    const originalProduct = originalSupplyOrder.products.find((p) => p._id === product._id);
                    if (
                        originalProduct &&
                        originalProduct.quantityGenerated !== null &&
                        originalProduct.quantityGenerated !== undefined &&
                        originalProduct.conditionning
                    ) {
                        // Calculer le nombre de colis : quantityGenerated / conditionning
                        const calculatedColis = Math.ceil(
                            originalProduct.quantityGenerated / originalProduct.conditionning,
                        );

                        // Mettre à jour le champ colis dans le formulaire
                        setValue(`supplyOrders.${supplyOrderIndex}.products.${productIndex}.colis`, calculatedColis);
                        updatedCount++;
                    }
                });
            }
        });

        if (updatedCount > 0) {
            toast.success(`${updatedCount} quantités commandées ont été mises à jour avec succès !`);
        } else {
            toast.warning(
                "Aucune quantité n'a pu être mise à jour. Vérifiez que les produits ont des données de prédiction valides.",
            );
        }
    }, [selectedSupplyOrderIds, methods, manualSupplyOrders]);

    const handleAddProducts = useCallback(
        async ({ productIds }: { productIds: string[] }) => {
            if (selectedSupplyOrderIds.length !== 1) {
                toast.error('Aucun fournisseur sélectionné');
                return;
            }

            const supplyOrderId = selectedSupplyOrderIds[0];
            if (!supplyOrderId) {
                toast.error('Commande fournisseur introuvable');
                return;
            }

            const existingProductIds = manualSupplyOrders.flatMap(({ products }) => products).map(({ _id }) => _id);

            if (existingProductIds.some((productId) => productIds.includes(productId))) {
                toast.error('Un des produits sélectionnés est déjà dans une des commandes');
                return;
            }

            const { errors } = await addProductsToManualSupplyOrder({
                variables: {
                    supplyOrderId,
                    productIds,
                },
            });

            await handleUpdateResults({ errors });
        },
        [selectedSupplyOrderIds, addProductsToManualSupplyOrder],
    );

    return (
        <FormProvider {...methods}>
            <Form onSubmit={onSubmit}>
                <Header>
                    <HeaderTitle>
                        <PageTitle page={PAGES.manualSupplyOrders} />
                    </HeaderTitle>
                    <ActionsAndSave
                        actionGroups={[]}
                        onSave={onSubmit}
                        saveDisabled={!isDirty || !isValid}
                        saveLoading={updateLoading || addProductsLoading}
                        saveTitle="Sauvegarder"
                        saveDataTest="submit-button"
                        saveRequiredPermissions={[TotadminPermission.SupplyOrdersManage]}
                    />
                </Header>
                <Content>
                    <ScrollableContent>
                        <SupplierStatusSection
                            manualSupplyOrders={manualSupplyOrders}
                            stockVariationData={stockVariationData}
                            selectedSupplyOrderIds={selectedSupplyOrderIds}
                            setSelectedSupplyOrderIds={setSelectedSupplyOrderIds}
                        />
                        <ProductsInfoSection
                            manualSupplyOrders={manualSupplyOrders}
                            stockVariationData={stockVariationData}
                            stockVariationLoading={stockVariationLoading || updateStockVariationsLoading}
                            stockVariationError={stockVariationError}
                            updateForecastsLoading={updateForecastsLoading}
                        />
                    </ScrollableContent>
                    <ManualSupplyOrdersActionBar
                        selectedSupplyOrderIds={selectedSupplyOrderIds}
                        setSelectedSupplyOrderIds={setSelectedSupplyOrderIds}
                        manualSupplyOrders={manualSupplyOrders}
                        handleUpdateQuantitiesFromPredictions={handleUpdateQuantitiesFromPredictions}
                        setUpdateStockVariationsLoading={setUpdateStockVariationsLoading}
                        setUpdateForecastsLoading={setUpdateForecastsLoading}
                        onAddProductsToSupplier={() => setIsAddProductsModalOpen(true)}
                    />
                </Content>
                <TrelloCardInfoPopup setIsOpen={setTrelloCardInfoPopup} isOpen={trelloCardInfoPopup} />
                <AddProductsToSupplierModal
                    isOpen={isAddProductsModalOpen}
                    onClose={() => setIsAddProductsModalOpen(false)}
                    onAddProducts={handleAddProducts}
                    supplierName={
                        manualSupplyOrders.find((so) => so._id === selectedSupplyOrderIds[0])?.supplier.name ?? null
                    }
                />
            </Form>
        </FormProvider>
    );
};

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    overflow: hidden;
`;

const Content = styled.div`
    position: relative;
    display: flex;
    flex: 1;
    overflow: hidden;
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;
